'use client';

import { memo, useState } from 'react';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { MatchCard, type MatchCardProps } from './MatchCard';
import {
  OverflowGroupClassification,
  TournamentFormat,
  type Availability,
  type Group,
  type Match,
  type Registration,
  type Schedule,
  type Team,
  type TeamStanding,
} from '@/types/tournament.model';
import {
  ChevronDown,
  ChevronRight,
  Trophy,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GenerateKnockoutModal } from './knockout/GenerateKnockoutModal';
import { getTeamName } from '@/utils/team-utils';
import {
  type DependentSideAvailability,
  dependentSideAvailabilityMessages,
  getDependentSideAvailability,
  getDependentTeamLabels,
} from '@/utils/dependent-match';
import { Tooltip } from '@/components/ui/tooltip';
import { PlayersNamesList } from '../PlayerNamesList';
import { useEloCategories } from '@/hooks/useEloCategories';
import {
  computeGroupClassifiedCount,
  groupLabel,
  isGroupResolved,
} from '@/utils/fixture-utils';

interface GroupStageProps {
  groups: Group[];
  classifiedPerGroup?: number;
  overflowGroupClassification?: OverflowGroupClassification;
  format?: TournamentFormat;
  tournamentId: string;
  /** Sport UID (e.g. PADEL) to resolve Elo categories for standings. */
  tournamentSportUid?: string;
  matchDurationMinutes?: number;
  registrations?: Registration[];
  tournamentSchedule?: Schedule[];
  hasKnockoutStage?: boolean;
  /** Whether the tournament is configured to end in a knockout bracket. Default: true. */
  expectsKnockoutStage?: boolean;
  onKnockoutGenerated?: () => void;
  onMatchResultUpdated?: () => void;
  /** Fixture status from FixtureSection. When false, result load/edit is blocked. */
  isFixtureActive?: boolean;
}

function GroupStageComponent({
  groups,
  classifiedPerGroup = 2,
  overflowGroupClassification,
  format,
  tournamentId,
  tournamentSportUid,
  matchDurationMinutes,
  registrations = [],
  tournamentSchedule = [],
  hasKnockoutStage = false,
  expectsKnockoutStage = true,
  onKnockoutGenerated,
  onMatchResultUpdated,
  isFixtureActive = true,
}: GroupStageProps) {
  const eloCategories = useEloCategories(tournamentSportUid);
  const isLong = format === TournamentFormat.LONG;

  // State to control which groups are expanded
  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>(
    // By default, expand only the first group
    groups.reduce((acc, _, index) => ({ ...acc, [index]: index === 0 }), {}),
  );

  const [showGenerateKnockoutModal, setShowGenerateKnockoutModal] =
    useState(false);
  const [isLoading] = useState(false);

  const availabilityByTeamId: Map<
    string,
    NonNullable<MatchCardProps['team1Availability']>
  > = (() => {
    const map = new Map<string, Availability[]>();
    registrations.forEach((reg) => {
      if (!reg.team?.id) return;
      if (reg.availability && reg.availability.length > 0) {
        map.set(reg.team.id, reg.availability);
      }
    });
    return map;
  })();

  const tournamentScheduleAvailability = (() => {
    const all = tournamentSchedule.flatMap((s) => s.schedule ?? []);
    return all;
  })();

  // Function to toggle the expansion state of a group
  const toggleGroup = (groupIndex: number): void => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupIndex]: !prev[groupIndex],
    }));
  };

  // Function to expand or collapse all groups
  const toggleAllGroups = (expand: boolean): void => {
    const newState = groups.reduce(
      (acc, _, index) => ({ ...acc, [index]: expand }),
      {},
    );
    setExpandedGroups(newState);
  };

  // Helper function to get the team object from an ID or an object
  const getTeamById = (
    teamIdOrObject: string | Team | undefined,
    group: Group,
  ): Team | undefined => {
    // If teamIdOrObject is a string (ID), search for the team in group.teamList
    if (typeof teamIdOrObject === 'string') {
      const teamItem = group.teamList.find(
        (teamItem) => teamItem.team.id === teamIdOrObject,
      );
      return teamItem?.team;
    }

    // If teamIdOrObject is already a Team object, return it directly
    if (typeof teamIdOrObject === 'object' && teamIdOrObject !== null) {
      return teamIdOrObject as Team;
    }

    return undefined;
  };

  // A dependent side still waiting for its source result can be taken by either team of that
  // match, so it shows whether both can make it, with copy naming that match. Any other side uses
  // its own team's availability and the default copy.
  const sideAvailabilityProps = (
    team: Team | undefined,
    dependentSide: DependentSideAvailability | undefined,
  ) =>
    dependentSide
      ? {
          availability: dependentSide.availability,
          messages: dependentSideAvailabilityMessages(dependentSide.sourceMatchNumber),
        }
      : {
          availability: team ? availabilityByTeamId.get(team.id) : undefined,
          messages: undefined,
        };

  // Get group standings from backend data
  const calculateStandings = (group: Group): TeamStanding[] => {
    // Use the stats provided by the backend instead of calculating locally
    const standings: TeamStanding[] = group.teamList.map((teamItem) => ({
      teamId: teamItem.team.id,
      played: teamItem.stats.matchesPlayed,
      won: teamItem.stats.wins,
      lost: teamItem.stats.losses,
      setsWon: teamItem.stats.setsWon,
      setsLost: 0, // We'll calculate this from match results if needed
      gamesWon: teamItem.stats.gamesWon ?? 0,
      gamesLost: teamItem.stats.gamesLost ?? 0,
      points: teamItem.stats.totalPoints,
    }));

    // Calculate sets lost from match results if we have matches
    if (group.matchList.length > 0) {
      const teamSetsLost: Record<string, number> = {};

      // Initialize sets lost for all teams
      group.teamList.forEach((teamItem) => {
        teamSetsLost[teamItem.team.id] = 0;
      });

      // Calculate sets lost from match results
      group.matchList.forEach((match) => {
        if (match.result && match.result.isValid) {
          let team1Id: string | undefined;
          let team2Id: string | undefined;

          if (typeof match.team1 === 'string') {
            team1Id = match.team1;
          } else if (match.team1 && typeof match.team1 === 'object') {
            team1Id = match.team1.id;
          }

          if (typeof match.team2 === 'string') {
            team2Id = match.team2;
          } else if (match.team2 && typeof match.team2 === 'object') {
            team2Id = match.team2.id;
          }

          // Update sets lost
          if (team1Id && teamSetsLost[team1Id] !== undefined) {
            teamSetsLost[team1Id] += match.result.team2Score;
          }
          if (team2Id && teamSetsLost[team2Id] !== undefined) {
            teamSetsLost[team2Id] += match.result.team1Score;
          }
        }
      });

      // Update standings with calculated sets lost
      standings.forEach((standing) => {
        standing.setsLost = teamSetsLost[standing.teamId] || 0;
      });
    }

    // Keep the backend order: teamList already arrives sorted by the tie-breaking
    // rules of the regulation (head-to-head first, then sets/games between the tied
    // teams). Re-sorting here by points and set difference would break those ties
    // differently from the bracket assignment.
    return standings;
  };

  // Calculate the total number of played and pending matches
  const getMatchStats = (
    group: Group,
  ): { total: number; played: number; pending: number } => {
    const total = group.matchList.length;
    const played = group.matchList.filter(
      (match) => match.result && match.result.isValid,
    ).length;
    const pending = total - played;
    return { total, played, pending };
  };

  const areAllGroupMatchesFinished = (): boolean => {
    return groups.every(isGroupResolved);
  };

  const handleResultUpdated = (): void => {
    // Check if all matches are finished to show the knockout generation modal
    if (areAllGroupMatchesFinished() && !hasKnockoutStage && expectsKnockoutStage) {
      setShowGenerateKnockoutModal(true);
    }

    // Notify the parent component that a result has been updated
    if (onMatchResultUpdated) {
      onMatchResultUpdated();
    }
  };

  const handleKnockoutGenerated = (): void => {
    if (onKnockoutGenerated) {
      onKnockoutGenerated();
    }
  };

  const getOverlapConflictsByMatch = (
    group: Group,
  ): Map<string, Set<string>> => {
    type TeamMatchSlot = {
      matchId: string;
      startMs: number;
      endMs: number;
    };

    const overlapsByMatch = new Map<string, Set<string>>();
    const slotsByTeamId = new Map<string, TeamMatchSlot[]>();

    const getTeamId = (team: Match['team1'] | Match['team2']): string | null => {
      if (!team) return null;
      return typeof team === 'string' ? team : team.id;
    };

    const markConflict = (matchId: string, teamId: string) => {
      if (!overlapsByMatch.has(matchId)) {
        overlapsByMatch.set(matchId, new Set<string>());
      }
      overlapsByMatch.get(matchId)?.add(teamId);
    };

    group.matchList.forEach((match) => {
      const team1Id = getTeamId(match.team1);
      const team2Id = getTeamId(match.team2);
      if (!team1Id || !team2Id) return;

      const startMs = new Date(match.startDate).getTime();
      if (Number.isNaN(startMs)) return;

      const parsedEndMs = match.endDate ? new Date(match.endDate).getTime() : NaN;
      const durationMsFromTournament =
        typeof matchDurationMinutes === 'number' && matchDurationMinutes > 0
          ? matchDurationMinutes * 60_000
          : 60_000;
      // If endDate is missing/invalid/same as start, use configured tournament duration.
      const endMs =
        Number.isNaN(parsedEndMs) || parsedEndMs <= startMs
          ? startMs + durationMsFromTournament
          : parsedEndMs;

      const slot: TeamMatchSlot = { matchId: match.id, startMs, endMs };

      const team1Slots = slotsByTeamId.get(team1Id) ?? [];
      team1Slots.push(slot);
      slotsByTeamId.set(team1Id, team1Slots);

      const team2Slots = slotsByTeamId.get(team2Id) ?? [];
      team2Slots.push(slot);
      slotsByTeamId.set(team2Id, team2Slots);
    });

    // Detect overlap per team with pairwise comparison.
    slotsByTeamId.forEach((slots, teamId) => {
      for (let i = 0; i < slots.length; i++) {
        for (let j = i + 1; j < slots.length; j++) {
          const a = slots[i];
          const b = slots[j];
          const hasOverlap = a.startMs < b.endMs && b.startMs < a.endMs;

          if (hasOverlap) {
            markConflict(a.matchId, teamId);
            markConflict(b.matchId, teamId);
          }
        }
      }
    });

    return overlapsByMatch;
  };

  return (
    <div className="space-y-8">
      {/* Buttons to expand/collapse all groups */}
      <div className="flex justify-end space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => toggleAllGroups(true)}
          disabled={isLoading}
        >
          Expandir todos
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => toggleAllGroups(false)}
          disabled={isLoading}
        >
          Colapsar todos
        </Button>
      </div>

      {/* Show a button to generate knockouts if all matches are finished and there's no knockout stage */}
      {areAllGroupMatchesFinished() && !hasKnockoutStage && expectsKnockoutStage && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                Todos los partidos de la fase de grupos han sido completados.
              </span>
            </div>
            <Button
              onClick={() => setShowGenerateKnockoutModal(true)}
              disabled={isLoading}
            >
              Generar fase de eliminatorias
            </Button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex items-center justify-center">
            <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent"></div>
            <span className="text-sm font-medium text-yellow-800">
              Actualizando información del torneo...
            </span>
          </div>
        </div>
      )}

      {groups.map((group, groupIndex) => {
        const isExpanded = expandedGroups[groupIndex];
        const standings = calculateStandings(group);
        const matchStats = getMatchStats(group);
        const overlapConflictsByMatch = getOverlapConflictsByMatch(group);
        const groupHasScheduleConflicts = overlapConflictsByMatch.size > 0;
        const groupHasForcedConflicts =
          isLong && group.matchList.some((match) => match.conflict);
        const isAmerican = format === TournamentFormat.AMERICAN;
        const isGroupOf4 = group.teamList.length === 4;
        const isExtraClassifierInGroupsOf4 =
          isGroupOf4 &&
          overflowGroupClassification ===
            OverflowGroupClassification.EXTRA_CLASSIFIER;
        const effectiveClassifiedPerGroup = computeGroupClassifiedCount(
          classifiedPerGroup,
          format,
          isExtraClassifierInGroupsOf4,
        );

        return (
          <div key={groupIndex} className="space-y-2">
            <Card
              className={
                !isExpanded && (groupHasScheduleConflicts || groupHasForcedConflicts)
                  ? '@container border-2 border-red-500'
                  : '@container'
              }
            >
              {/* Group header with button to expand/collapse */}
              <div
                className={`cursor-pointer px-4 py-4 ${isExpanded ? 'border-b' : ''}`}
                onClick={() => toggleGroup(groupIndex)}
              >
                {isExpanded ? (
                  /* Expanded: just chevron + title */
                  <div className="flex items-center gap-2">
                    <ChevronDown className="h-5 w-5 shrink-0 text-gray-500" />
                    <CardTitle className="text-lg">
                      {groupLabel(groupIndex)}
                    </CardTitle>
                  </div>
                ) : (
                  /* Collapsed: container-query responsive layout */
                  <div className="flex flex-col gap-2 @[760px]:flex-row @[760px]:items-center @[760px]:justify-between">
                    {/* Head: chevron + title + compact meta */}
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1">
                      <ChevronRight className="h-5 w-5 shrink-0 text-gray-500" />
                      <span className="whitespace-nowrap text-lg font-bold">
                        {groupLabel(groupIndex)}
                      </span>
                      <span className="text-sm text-gray-500">
                        {group.teamList.length} equipos
                        <span className="mx-1.5 text-gray-300">·</span>
                        {matchStats.played}/{matchStats.total} partidos
                      </span>
                    </div>

                    {/* Trophy + classified chips */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Trophy className="h-4 w-4 shrink-0 text-gray-400" />
                      {standings
                        .slice(0, effectiveClassifiedPerGroup)
                        .map((standing) => {
                          const allFinished = isGroupResolved(group);
                          const team = group.teamList.find(
                            (t) => t.team.id === standing.teamId,
                          );
                          return (
                            <span
                              key={standing.teamId}
                              className={`inline-flex items-center whitespace-nowrap rounded-full border px-3 py-1 text-xs font-semibold ${
                                allFinished
                                  ? 'border-green-600 text-green-700'
                                  : 'border-gray-300 text-gray-600'
                              }`}
                            >
                              {getTeamName(team?.team)}
                            </span>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>

              {/* Expanded content */}
              {isExpanded && (
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    {isExtraClassifierInGroupsOf4 && !isAmerican && (
                      <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                        En este grupo de 4 equipos <span className="font-semibold">clasifican 3</span>.
                      </div>
                    )}
                    {/* Group standings table */}
                    <div className="overflow-x-auto">
                      <div className="min-w-[840px]">
                        {/* Table Header */}
                        <div className="grid grid-cols-[48px_minmax(240px,1.15fr)_repeat(8,56px)] gap-2 border-b pb-2 font-medium text-gray-700">
                          <Tooltip
                            content="Posición - Posición del equipo en la tabla de posiciones del grupo"
                            className="w-[50px]"
                          >
                            Pos
                          </Tooltip>
                          <div>Equipo</div>
                          <Tooltip
                            content="Partidos Jugados - Total de partidos disputados por el equipo"
                            className="text-center"
                          >
                            PJ
                          </Tooltip>
                          <Tooltip
                            content="Partidos Ganados - Cantidad de partidos que el equipo ha ganado"
                            className="text-center"
                          >
                            PG
                          </Tooltip>
                          <Tooltip
                            content="Partidos Perdidos - Cantidad de partidos que el equipo ha perdido"
                            className="text-center"
                          >
                            PP
                          </Tooltip>
                          <Tooltip
                            content="Sets a Favor - Total de sets ganados por el equipo en todos sus partidos"
                            className="text-center"
                          >
                            SF
                          </Tooltip>
                          <Tooltip
                            content="Sets Perdidos - Total de sets perdidos por el equipo en todos sus partidos"
                            className="text-center"
                          >
                            SP
                          </Tooltip>
                          <Tooltip
                            content="Games Ganados - Total de games ganados por el equipo en todos sus partidos"
                            className="text-center"
                          >
                            GG
                          </Tooltip>
                          <Tooltip
                            content="Games Perdidos - Total de games perdidos por el equipo en todos sus partidos"
                            className="text-center"
                          >
                            GP
                          </Tooltip>
                          <Tooltip
                            content="Puntos - Puntos totales obtenidos (3 puntos por victoria, 1 punto por derrota)"
                            className="sticky right-0 z-20 bg-white text-center"
                          >
                            Pts
                          </Tooltip>
                        </div>

                        {/* Table Body */}
                        <div className="divide-y">
                          {standings.map((standing, index) => {
                            const isClassified =
                              index < effectiveClassifiedPerGroup;
                            const isLastClassified =
                              index === effectiveClassifiedPerGroup - 1;
                            const allMatchesFinished =
                              isGroupResolved(group);
                            const team = group.teamList.find(
                              (teamItem) =>
                                teamItem.team.id === standing.teamId,
                            );
                            const rowBg = isClassified
                              ? allMatchesFinished
                                ? 'bg-green-50'
                                : 'bg-gray-50'
                              : 'bg-white';

                            return (
                              <div
                                key={standing.teamId}
                                className={`relative grid grid-cols-[48px_minmax(240px,1.15fr)_repeat(8,56px)] gap-2 py-2 ${
                                  rowBg
                                } ${isLastClassified ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:bg-gray-300 after:content-[""]' : ''} `}
                              >
                                <div className="font-medium">{index + 1}</div>
                                <div className="min-w-0">
                                  <PlayersNamesList
                                    players={team?.team.players}
                                    fallbackName={getTeamName(team?.team)}
                                    className="text-sm font-medium text-gray-900"
                                    rankingMeta={
                                      tournamentSportUid
                                        ? {
                                            sportUid: tournamentSportUid,
                                            eloCategories,
                                          }
                                        : undefined
                                    }
                                  />
                                </div>
                                <div className="text-center">
                                  {standing.played}
                                </div>
                                <div className="text-center">
                                  {standing.won}
                                </div>
                                <div className="text-center">
                                  {standing.lost}
                                </div>
                                <div className="text-center">
                                  {standing.setsWon}
                                </div>
                                <div className="text-center">
                                  {standing.setsLost}
                                </div>
                                <div className="text-center">
                                  {standing.gamesWon}
                                </div>
                                <div className="text-center">
                                  {standing.gamesLost}
                                </div>
                                <div
                                  className={`sticky right-0 z-10 text-center font-bold ${rowBg}`}
                                >
                                  {standing.points}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Group matches */}
                    <div>
                      <h4 className="mb-3 font-medium">Partidos del grupo</h4>
                      {isLong && group.matchList.length === 0 ? (
                        <p className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center text-sm text-muted-foreground">
                          Los partidos se generan fecha a fecha. Abrí la primera fecha desde la
                          sección Fechas.
                        </p>
                      ) : (
                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {group.matchList.map((match, matchIndex) => {
                          const team1 = getTeamById(match.team1, group);
                          const team2 = getTeamById(match.team2, group);

                          // Dependent matches (2+2 format) have placeholder teams with no players
                          // until their source results are in; label them instead of rendering
                          // "Unknown team".
                          const dependentLabels = getDependentTeamLabels(
                            match,
                            group.matchList,
                          );
                          const isDependentMatch =
                            dependentLabels.team1 !== undefined;

                          const dependentAvailability = getDependentSideAvailability(
                            match,
                            group.matchList,
                            availabilityByTeamId,
                          );
                          const side1 = sideAvailabilityProps(team1, dependentAvailability.team1);
                          const side2 = sideAvailabilityProps(team2, dependentAvailability.team2);

                          // Only render if we have valid teams — a dependent match is the one
                          // legitimate case for a missing team.
                          if (!isDependentMatch && (!team1 || !team2)) {
                            return null; // Don't render this match if any team is missing
                          }

                          const conflictTeamIds =
                            overlapConflictsByMatch.get(match.id) ?? new Set();
                          const conflictTeamName =
                            team1 && conflictTeamIds.has(team1.id)
                              ? getTeamName(team1)
                              : team2 && conflictTeamIds.has(team2.id)
                                ? getTeamName(team2)
                                : null;

                          return (
                            <MatchCard
                              key={match.id}
                              match={{
                                ...match,
                                result: match.result || {
                                  isValid: false,
                                  team1Score: 0,
                                  team2Score: 0,
                                },
                              }}
                              team1Name={
                                dependentLabels.team1 ?? getTeamName(team1)
                              }
                              team2Name={
                                dependentLabels.team2 ?? getTeamName(team2)
                              }
                              matchNumber={matchIndex + 1}
                              tournamentId={tournamentId}
                              canLoadResults={isFixtureActive}
                              onResultUpdated={handleResultUpdated}
                              groupTeamCount={group.teamList.length}
                              totalMatchesInGroup={group.matchList.length}
                              team1Object={team1}
                              team2Object={team2}
                              team1Availability={side1.availability}
                              team2Availability={side2.availability}
                              team1AvailabilityMessages={side1.messages}
                              team2AvailabilityMessages={side2.messages}
                              tournamentScheduleAvailability={
                                tournamentScheduleAvailability
                              }
                              scheduleConflictMessage={
                                conflictTeamName
                                  ? `El equipo ${conflictTeamName} tiene conflicto de horarios`
                                  : undefined
                              }
                              forcedConflict={isLong ? Boolean(match.conflict) : undefined}
                              forcedMissingAvailability={
                                isLong ? Boolean(match.conflictMissingAvailability) : undefined
                              }
                              isProposed={isLong ? match.scheduled === false : undefined}
                              sportUid={tournamentSportUid}
                              eloCategories={eloCategories}
                            />
                          );
                        })}
                      </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          </div>
        );
      })}

      {/* Modal to generate the knockout stage */}
      <GenerateKnockoutModal
        open={showGenerateKnockoutModal}
        onOpenChange={setShowGenerateKnockoutModal}
        tournamentId={tournamentId}
        onSuccess={handleKnockoutGenerated}
      />
    </div>
  );
}

// Memoized: FixtureSection keeps this mounted while the knockout tab is
// showing, so unrelated parent state changes must not re-render the groups.
export const GroupStage = memo(GroupStageComponent);
